/**
 * The skills a person sees, from the same resolver the agents use (RFC 0334 3g).
 *
 * WHY `resolveSkills` AND NOT OUR OWN MERGE. `/` and the Skills section must
 * list exactly the set an agent in the project gets, under the same ids: `/weekly`
 * only inlines Weekly's skill if the worker's set also calls it `weekly`. A
 * second merge here would be a second place that decides precedence and the
 * `app-` prefix, and the two would drift.
 *
 * EVERY MOD SKILL IS SHOWN. An agent gets a Mod skill only when it holds that
 * skill's tools, but a person reading the list is not a session and holds none,
 * so the list passes every tool any Mod skill names.
 *
 * SHADOWED SKILLS SHOW ONCE, as the nearer one, with the one it replaces on
 * `shadows` so the row can still reach it.
 */

import {
  appSkillSources,
  MOD_SKILLS,
  type ModSkill,
  projectWorldSkills,
  resolveSkills,
  type SkillEntry,
  type SkillScope,
} from '@mod/core/model'

/** One skill as `/` and the sidebar show it. */
export interface ShownSkill extends SkillEntry {
  /** The World or Mod skill this one replaces here, when it replaces one. */
  shadows?: SkillEntry
}

/** The order `/` lists and the sidebar groups: nearest first, Mod's last. */
export const SCOPE_ORDER: readonly SkillScope[] = [
  'project',
  'world',
  'app',
  'mod',
]

/** The word a row carries for where its skill lives. */
export const SCOPE_LABEL: Record<SkillScope, string> = {
  project: 'Project',
  world: 'World',
  app: 'App',
  mod: 'Mod',
}

/** A Mod skill's text ships in core; its document id says so. */
export const isModSkill = (s: { documentId: string }): boolean =>
  s.documentId.startsWith('mod:')

export function shownSkills(
  world: unknown,
  project: unknown,
  modSkills: readonly ModSkill[] = MOD_SKILLS,
): ShownSkill[] {
  const tools = [...new Set(modSkills.flatMap((m) => m.tools))]
  const set = resolveSkills(
    world,
    project,
    appSkillSources(world, project),
    tools,
    { modSkills },
  )
  const worldById = new Map(
    projectWorldSkills(world).map((s) => [
      s.skillId,
      { ...s, scope: 'world' as const },
    ]),
  )
  const modById = new Map(
    modSkills.map((m) => [
      m.skillId,
      {
        skillId: m.skillId,
        title: m.title,
        description: m.description,
        documentId: m.documentId,
        scope: 'mod' as const,
      },
    ]),
  )
  const rank = (s: SkillEntry) => SCOPE_ORDER.indexOf(s.scope)
  return set
    .map((s): ShownSkill => {
      const shadows =
        s.scope === 'project'
          ? (worldById.get(s.skillId) ?? modById.get(s.skillId))
          : s.scope === 'world'
            ? modById.get(s.skillId)
            : undefined
      return shadows ? { ...s, shadows } : s
    })
    .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title))
}

/**
 * The skills a person names with `/` and finds in the Skills section: the
 * World's and the project's, the ones people write (RFC 0334 3g).
 *
 * An app's skill arrives by itself when the app is open, and is read from the
 * app's header tile and menus. A Mod skill is for the agent to load, and is
 * read in Settings. Listing either here is a second way to reach the same
 * skill, in a menu that is for choosing yours.
 */
export function pickableSkills<T extends { scope: SkillScope }>(
  skills: readonly T[],
): T[] {
  return skills.filter((s) => s.scope === 'project' || s.scope === 'world')
}

/** The skill an installed app contributes, or undefined when it has none. */
export function appSkillOf(
  skills: readonly ShownSkill[],
  appId: string,
): ShownSkill | undefined {
  return skills.find((s) => s.scope === 'app' && s.appId === appId)
}
